<template>
   <nav
      class="flex flex-col shadow-std"
      data-cy="flowsel-top-nav"
      :style="`margin-top: ${isAccountRepBannerVisible ? '2.625rem' : '0px'}`"
   >
      <!-- logo and study name -->
      <div class="h-20 border-b-2 border-grey-10">
         <div class="container flex h-full items-center justify-between">
            <!-- Company logo (without link) -->
            <CompanyLogo />

            <!-- Menu options -->
            <div v-if="supportButton" class="flex items-center">
               <!-- Support link -->
               <DefaultButton
                  v-if="supportButton.type === 'button' && intercom.isActive"
                  data-cy="flowsel-nav-help-btn"
                  size="sm"
                  :color="Theme.Black"
                  :text="supportButton.text"
                  style-type="ghost"
                  @click="intercom.open()"
               />
               <LinkButton
                  v-else-if="supportButton.type === 'link'"
                  :isExternal="supportButton.isExternal"
                  v-bind="supportButton"
                  size="sm"
                  style-type="ghost"
                  :color="Theme.Black"
               />
            </div>
         </div>
      </div>
      <!-- Flow name -->
      <div
         v-if="flowName"
         class="container flex min-h-10 flex-col items-start justify-center py-2 md:min-h-14 md:flex-row md:items-center md:justify-start md:py-0"
      >
         <span v-if="flowName" data-cy="flowsel-nav-flow-name">{{ flowName }}</span>
         <!-- Resume code -->
         <div
            v-if="flowParticipationStore.resumeCode"
            class="flex flex-row items-center justify-center max-lg:min-w-52 md:ml-6 lg:min-w-60"
         >
            <span class="max-lg:hidden lg:mr-6">|</span>
            <span class="grow"
               >{{ $t("survey.resume.code") }}&nbsp;
               <span :class="{ 'text-bolder': isResumeCodeVisible }">{{ displayedResumeCode }}</span>
            </span>
            <DefaultButton
               class="ml-2"
               size="sm"
               style-type="ghost"
               :color="Theme.BrandDark"
               rounded-edges="md"
               isIcon
               @click="toggleResumeCodeVisible"
            >
               <IconEyeClosed v-if="isResumeCodeVisible" />
               <IconEyeOpen v-else />
            </DefaultButton>
            <DefaultButton
               class="ml-1"
               size="sm"
               style-type="ghost"
               :color="Theme.BrandDark"
               rounded-edges="md"
               isIcon
               @click="showResumeCodeTooltip"
            >
               <IconLightExclamationCircle />
            </DefaultButton>
         </div>
      </div>
   </nav>
</template>

<script setup lang="ts">
import { intercomKey } from "@/plugins/useIntercom";
import { useToastKey } from "@/plugins/useToast";
import { useApiRequest } from "@/composables/useApiRequest";
import ContentApi from "@/data/api-layer/contentApi";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { Theme } from "@/types";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";
import { useUserStore } from "@/stores/userStore";
import { type FlowStudy } from "@/data/api-layer/flowsApi";

interface Props {
   study: FlowStudy | null;
   flowName: string;
   flowId: string;
}

// declarations
// -----------------------------------------
const { isLoading, execRequest, loadErrors } = useApiRequest();
const intercom = inject(intercomKey);
const useToast = inject(useToastKey);
const { t: $t } = useI18n();
const userStore = useUserStore();
const flowParticipationStore = useFlowParticipationStore();

const supportButton = ref(null);
defineProps<Props>();

// state
const isResumeCodeVisible = ref(false);

// lifecycle
// -----------------------------------------
onMounted(async () => {
   const response = await execRequest(() => ContentApi.getFlowsel());
   if (!response) return;

   // if the support button is defined by the admin, show it
   if (response.content.topnav?.support) {
      supportButton.value = response.content.topnav.support;
   }
});

// computed
// -----------------------------------------
const displayedResumeCode = computed(() => {
   return isResumeCodeVisible.value
      ? flowParticipationStore.resumeCode
      : "*".repeat(flowParticipationStore.resumeCode.length);
});

// is the account representation banner showing, if so, we need to make some CSS changes
const isAccountRepBannerVisible = computed(() => {
   return userStore.isImpersonating || userStore.isAccountRepresented;
});

// methods
// -----------------------------------------
async function showResumeCodeTooltip() {
   await useToast({
      position: "center",
      width: 592,
      html: `
         <div class="text-center p-10">
            <p>${$t("survey.resume.code_tooltip")}</p>
         </div>
      `,
      showConfirmButton: false,
      showDenyButton: false,
      showCancelButton: false,
      allowEnterKey: true,
   });
}

function toggleResumeCodeVisible() {
   isResumeCodeVisible.value = !isResumeCodeVisible.value;
}
</script>

<style scoped lang="scss">
.btn-bg {
   @apply absolute left-0 top-0 w-full h-full rounded-full transition-opacity duration-300 ease-in-out;
}
</style>
